import { useState, useMemo, useCallback } from "react";
import { usePagination } from "../../../../hooks/usePagination";
import { useTenant } from "../../../../hooks/useTenant";
import { toast } from "react-toastify";
import { ConfirmDialog } from "../../../../components/Common";
import LevelService from "../../../../services/level_service";

/**
 * useLevels
 * Hook de lógica para o módulo de Níveis (Levels).
 * Utiliza o LevelService para persistência.
 */
export const useLevels = () => {
    const { idTenant, user } = useTenant();
    const [isSaving, setIsSaving] = useState(false);

    const paginationOptions = useMemo(() => ({
        pageSize: 50,
        orderField: "order",
        orderDirection: "asc"
    }), []);

    // Paginação dos Níveis
    const { data: levels, loading, error, refresh } = usePagination(
        `tenants/${idTenant}/levels`,
        idTenant,
        paginationOptions
    );

    /**
     * Salvar um Nível
     */
    const saveLevel = useCallback(async (formData) => {
        if (!idTenant) return;
        setIsSaving(true);

        try {
            await LevelService.save(formData, idTenant, user, levels);
            toast.success(formData.id ? "Nível atualizado!" : "Novo nível cadastrado!");
            refresh();
            return true;
        } catch (err) {
            console.error("Erro ao salvar nível:", err);
            toast.error("Erro ao salvar nível.");
            return false;
        } finally {
            setIsSaving(false);
        }
    }, [idTenant, user, levels, refresh]);

    /**
     * Excluir um Nível
     */
    const deleteLevel = useCallback(async (levelId) => {
        if (!idTenant) return;

        const levelToDelete = levels.find(l => l.id === levelId);

        const confirmed = await ConfirmDialog({
            title: "Excluir Nível?",
            text: `Tem certeza que deseja excluir "${levelToDelete?.name || 'este nível'}"? Esta ação não pode ser desfeita.`,
            confirmButtonText: "Sim, excluir",
            cancelButtonText: "Cancelar"
        });

        if (!confirmed) return;

        try {
            await LevelService.delete(levelId, idTenant, user, levelToDelete?.name);
            toast.success("Nível removido!");
            refresh();
            return true;
        } catch (err) {
            console.error("Erro ao excluir nível:", err);
            toast.error("Não foi possível excluir.");
            return false;
        }
    }, [idTenant, user, levels, refresh]);

    /**
     * Reordenar Níveis
     */
    const reorderLevels = useCallback(async (startIndex, endIndex) => {
        if (!idTenant) return;

        const items = Array.from(levels);
        const [reorderedItem] = items.splice(startIndex, 1);
        items.splice(endIndex, 0, reorderedItem);

        try {
            await LevelService.reorder(items, idTenant, user);
            refresh();
            return true;
        } catch (err) {
            console.error("Erro ao reordenar níveis:", err);
            toast.error("Erro ao salvar nova ordem.");
            return false;
        }
    }, [idTenant, user, levels, refresh]);

    return {
        levels,
        loading,
        error,
        isSaving,
        actions: {
            saveLevel,
            deleteLevel,
            reorderLevels,
            refresh
        }
    };
};
